import { useRef } from "react";
import { useTransform, useScroll, useSpring } from "framer-motion";
import { Box } from "@chakra-ui/react";
import { MotionGrid as Grid, MotionBox, appearBelow } from "utils/motion";
import Text from "components/base/Text";
import Description from "components/base/Description";
import Image from "components/base/Image";
import Container from "components/base/Container";

const height = ["200px", "300px", "300px", "460px"];

const revealLeftVariants = {
  hidden: { opacity: 1, x: 0 },
  visible: { opacity: 1, x: -840, transition: { duration: 1.0 } },
};

const revealRightVariants = {
  hidden: { opacity: 1, x: 0 },
  visible: { opacity: 1, x: 840, transition: { duration: 1.0 } },
};

const CaptionBox = ({ title, desc }) => (
  <Box
    position="absolute"
    bottom={["16px", null, "32px"]}
    left={["21px", null, "42px"]}
    w="250px"
  >
    <Text type="headline_md" fontWeight="bold" color="white">
      {title}
    </Text>
    <Text type="title_md" color="white">
      {desc}
    </Text>
  </Box>
);

export default function TextImageColumns({
  topTitle,
  topDescription,
  topContent,
  topImage,
  topImageObjectPosition,
  topImage2,
  topCaption,
  topCaptionDesc,
  topCaptionDesc2,
  bottomTitle,
  bottomDescription,
  bottomContent,
  bottomImage,
  bottomCaption,
  bottomCaptionDesc,
  isInverted,
  animationId,
  bg,
  ...props
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["-100%", "100%"],
  });
  const springScroll = useSpring(scrollYProgress, {
    stiffness: 176,
    damping: 26,
  });
  const y = useTransform(springScroll, [0, 1], ["0px", "-40px"]);

  return (
    <Box
      ref={ref}
      width="100%"
      bg={bg || (isInverted ? "black.900" : "white")}
      mt="-1px"
    >
      <Container centerContent {...props} pb={0} px={[6, null, 20]}>
        <Grid
          variants={appearBelow}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          width="100%"
          templateColumns={["1fr", null, null, "1fr 0.75fr"]}
          alignItems="center"
          gap={[4, null, 8, 16]}
          mb={[12, null, 20]}
          overflow="hidden"
        >
          <Box id={`box-${animationId}`} position="relative" minHeight={height}>
            {topImage2 && (
              <MotionBox
                position="absolute"
                w="100%"
                animate={{ opacity: 0 }}
                transition={{
                  repeat: Infinity,
                  repeatDelay: 3,
                  repeatType: "reverse",
                  duration: 1,
                }}
                height={height}
                zIndex={2}
              >
                <Image
                  src={topImage2}
                  minHeight={height}
                  minWidth={["100%", "320px"]}
                  objectFit="cover"
                />
              </MotionBox>
            )}
            {topImage && (
              <MotionBox position="relative" style={{ y }}>
                <MotionBox
                  position="absolute"
                  minHeight={height}
                  zIndex={1}
                  bg={bg || (isInverted ? "black.700" : "white")}
                  w="100%"
                  variants={revealLeftVariants}
                />
                <Image
                  src={topImage}
                  minHeight={height}
                  minWidth={["100%", "320px"]}
                  objectFit="cover"
                  objectPosition={topImageObjectPosition || "center"}
                />
              </MotionBox>
            )}
            <CaptionBox title={topCaption} desc={topCaptionDesc} />
            {topCaptionDesc2 && (
              <MotionBox variants={appearBelow}>
                <Text type="p_sm" color="white" mt="-12px" opacity={0.75}>
                  {topCaptionDesc2}
                </Text>
              </MotionBox>
            )}
          </Box>
          <Box py={[0, null, 2, 12]}>
            {topContent ? (
              topContent
            ) : (
              <>
                <Text
                  type="headline_sm"
                  mb="sm"
                  color={isInverted ? "white" : "black.900"}
                >
                  {topTitle}
                </Text>
                <Text type="p_md" color={isInverted ? "white" : "black.900"}>
                  {topDescription}
                </Text>
              </>
            )}
          </Box>
        </Grid>
        {(bottomTitle || bottomDescription || bottomContent) && bottomImage && (
          <Grid
            overflow="hidden"
            variants={appearBelow}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            width="100%"
            templateColumns={["1fr", null, null, "0.75fr 1fr"]}
            alignItems="center"
            gap={[4, null, 8, 16]}
            pb={[12, 12, 0]}
          >
            <Box py={[0, null, 2, 12]}>
              {bottomContent ? (
                bottomContent
              ) : (
                <>
                  <Text
                    type="headline_sm"
                    mb="sm"
                    color={isInverted ? "white" : "black.900"}
                  >
                    {bottomTitle}
                  </Text>
                  <Text type="p_md" color={isInverted ? "white" : "black.900"}>
                    {bottomDescription}
                  </Text>
                </>
              )}
            </Box>
            <MotionBox
              order={[-1, null, null, 1]}
              position="relative"
              minHeight={height}
              style={{ y }}
            >
              <MotionBox
                position="absolute"
                minHeight={height}
                zIndex={1}
                bg={bg || (isInverted ? "black.700" : "white")}
                w="100%"
                variants={revealRightVariants}
              />
              <Image
                src={bottomImage}
                minHeight={height}
                minWidth={["100%", "320px"]}
                objectFit="cover"
              />
              <CaptionBox title={bottomCaption} desc={bottomCaptionDesc} />
            </MotionBox>
          </Grid>
        )}
      </Container>
    </Box>
  );
}
